/**
 * IntegrationDocsPage.tsx — Comprehensive product reference & integration guide
 *
 * Full-page documentation experience with:
 *   - Left sidebar catalog/index with category + chapter navigation
 *   - Styled content area matching the SlidePanel integration guide
 *   - Multi-format export (Markdown, HTML, JSON)
 *   - Search across all sections
 *   - Copy-to-clipboard for all code blocks
 *
 * @module packages/billing-admin/src/pages/IntegrationDocsPage
 * @since 2026-06-20 (Platform Upgrade)
 */
import React, { useState, useRef, useMemo, useCallback, useEffect } from 'react';
import AdminLayout from '../components/AdminLayout';
import { GUIDE_CATEGORIES, type GuideCategory, type GuideChapter, type GuideCodeBlock } from '../guides/referenceGuide';

// ---------------------------------------------------------------------------
// Export helpers
// ---------------------------------------------------------------------------

function guideToMarkdown(): string {
  const lines: string[] = ['# Rule42 Billing — Reference Guide\n'];
  for (const cat of GUIDE_CATEGORIES) {
    lines.push(`\n## ${cat.title}\n`);
    for (const ch of cat.chapters) {
      lines.push(`\n### ${ch.icon} ${ch.title}\n`);
      lines.push(`${ch.description}\n`);
      for (const sec of ch.sections) {
        lines.push(`\n#### ${sec.title}\n`);
        lines.push(sec.content + '\n');
        if (sec.codeBlocks) {
          for (const cb of sec.codeBlocks) {
            if (cb.caption) lines.push(`\n*${cb.caption}*\n`);
            lines.push(`\n\`\`\`${cb.language}\n${cb.code}\n\`\`\`\n`);
          }
        }
      }
    }
  }
  return lines.join('\n');
}

function guideToHtml(): string {
  const escape = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  let html = '<!DOCTYPE html>\n<html lang="en"><head><meta charset="UTF-8"><title>Rule42 Billing — Reference Guide</title>';
  html += '<style>body{font-family:system-ui,sans-serif;max-width:900px;margin:2rem auto;padding:0 1rem;color:#1e293b;line-height:1.7}';
  html += 'h1{color:#0e7490;border-bottom:2px solid #e2e8f0;padding-bottom:.5rem}h2{color:#334155;margin-top:2.5rem}h3{color:#0e7490}h4{color:#475569}';
  html += 'pre{background:#0f172a;color:#e2e8f0;padding:1rem;border-radius:8px;overflow-x:auto;font-size:13px}';
  html += 'code{font-family:"SF Mono",Menlo,monospace}.caption{color:#64748b;font-size:13px;font-style:italic;margin-bottom:4px}';
  html += '.section{margin:1.5rem 0;padding:1.5rem;border:1px solid #e2e8f0;border-radius:12px;background:#f8fafc}</style></head><body>';
  html += '<h1>Rule42 Billing — Reference Guide</h1>';
  for (const cat of GUIDE_CATEGORIES) {
    html += `<h2>${escape(cat.title)}</h2>`;
    for (const ch of cat.chapters) {
      html += `<h3>${ch.icon} ${escape(ch.title)}</h3><p>${escape(ch.description)}</p>`;
      for (const sec of ch.sections) {
        html += `<div class="section"><h4>${escape(sec.title)}</h4>`;
        html += `<p>${escape(sec.content).replace(/\n/g, '<br>')}</p>`;
        if (sec.codeBlocks) {
          for (const cb of sec.codeBlocks) {
            if (cb.caption) html += `<p class="caption">${escape(cb.caption)}</p>`;
            html += `<pre><code>${escape(cb.code)}</code></pre>`;
          }
        }
        html += '</div>';
      }
    }
  }
  html += '</body></html>';
  return html;
}

function guideToJson(): string {
  return JSON.stringify(GUIDE_CATEGORIES, null, 2);
}

function downloadFile(content: string, filename: string, mimeType: string): void {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ---------------------------------------------------------------------------
// Code Block component
// ---------------------------------------------------------------------------

function CodeBlock({ block, globalCopyIdx, idx, onCopy }: {
  block: GuideCodeBlock;
  globalCopyIdx: number | null;
  idx: number;
  onCopy: (code: string, idx: number) => void;
}) {
  return (
    <div className="mt-3">
      {block.caption && (
        <p className="mb-1.5 text-xs font-medium text-slate-500">{block.caption}</p>
      )}
      {block.file && (
        <p className="mb-1 text-[10px] font-mono text-slate-400">{block.file}</p>
      )}
      <div className="group relative">
        <button
          onClick={() => onCopy(block.code, idx)}
          className="absolute right-2 top-2 z-10 rounded border border-slate-600 bg-slate-800 px-2 py-1 text-[10px] font-medium text-slate-300 opacity-0 transition group-hover:opacity-100 hover:bg-slate-700"
        >
          {globalCopyIdx === idx ? '✓ Copied' : 'Copy'}
        </button>
        <pre className="overflow-x-auto rounded-lg bg-slate-900 p-4 text-[13px] leading-relaxed text-slate-200">
          <code>{block.code}</code>
        </pre>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function IntegrationDocsPage(): React.JSX.Element {
  const [activeChapterId, setActiveChapterId] = useState(GUIDE_CATEGORIES[0]?.chapters[0]?.id ?? '');
  const [expandedCats, setExpandedCats] = useState<Set<string>>(() => new Set(GUIDE_CATEGORIES.map(c => c.id)));
  const [searchQuery, setSearchQuery] = useState('');
  const [copiedIdx, setCopiedIdx] = useState<number | null>(null);
  const [showExport, setShowExport] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  // Build a flat map of chapterId → { category, chapter }
  const chapterMap = useMemo(() => {
    const m = new Map<string, { cat: GuideCategory; chapter: GuideChapter }>();
    for (const cat of GUIDE_CATEGORIES) {
      for (const ch of cat.chapters) {
        m.set(ch.id, { cat, chapter: ch });
      }
    }
    return m;
  }, []);

  // Search filtering
  const filteredCategories = useMemo(() => {
    if (!searchQuery.trim()) return GUIDE_CATEGORIES;
    const q = searchQuery.toLowerCase();
    return GUIDE_CATEGORIES.map(cat => ({
      ...cat,
      chapters: cat.chapters.filter(ch =>
        ch.title.toLowerCase().includes(q) ||
        ch.description.toLowerCase().includes(q) ||
        ch.sections.some(s =>
          s.title.toLowerCase().includes(q) ||
          s.content.toLowerCase().includes(q)
        )
      ),
    })).filter(cat => cat.chapters.length > 0);
  }, [searchQuery]);

  const activeEntry = chapterMap.get(activeChapterId);

  const handleCopy = useCallback((code: string, idx: number) => {
    void navigator.clipboard.writeText(code);
    setCopiedIdx(idx);
    setTimeout(() => setCopiedIdx(null), 2000);
  }, []);

  const toggleCat = useCallback((catId: string) => {
    setExpandedCats(prev => {
      const next = new Set(prev);
      if (next.has(catId)) next.delete(catId);
      else next.add(catId);
      return next;
    });
  }, []);

  // Scroll content to top on chapter change
  useEffect(() => {
    contentRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  }, [activeChapterId]);

  // Global code block index for copy state
  let codeIdx = 0;

  return (
    <AdminLayout title="Reference Guide">
    <div className="-mx-8 -my-8 flex" style={{ height: 'calc(100vh - 88px)' }}>
      {/* ─── Left Sidebar / Index ────────────────────────────────────── */}
      <aside className="flex w-72 flex-shrink-0 flex-col border-r border-slate-200 bg-white">
        {/* Header */}
        <div className="border-b border-slate-100 px-5 py-4">
          <h1 className="text-sm font-bold tracking-wide text-cyan-700 uppercase">Reference Guide</h1>
          <p className="mt-0.5 text-[11px] text-slate-400">User Guide & Integration Docs</p>
        </div>

        {/* Search */}
        <div className="border-b border-slate-100 px-4 py-3">
          <div className="relative">
            <svg className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-slate-400" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
            </svg>
            <input
              type="text"
              placeholder="Search guide..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full rounded-lg border border-slate-200 bg-slate-50 py-1.5 pl-8 pr-3 text-xs text-slate-700 placeholder:text-slate-400 focus:border-cyan-400 focus:outline-none focus:ring-1 focus:ring-cyan-400"
            />
          </div>
        </div>

        {/* Category / Chapter tree */}
        <nav className="flex-1 overflow-y-auto px-3 py-2">
          {filteredCategories.map((cat) => (
            <div key={cat.id} className="mb-1">
              <button
                onClick={() => toggleCat(cat.id)}
                className="flex w-full items-center gap-1.5 rounded-md px-2 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-400 hover:bg-slate-50 hover:text-slate-600"
              >
                <svg
                  className={`h-3 w-3 transition-transform ${expandedCats.has(cat.id) ? 'rotate-90' : ''}`}
                  fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                </svg>
                {cat.title}
              </button>
              {expandedCats.has(cat.id) && (
                <div className="ml-2 space-y-0.5">
                  {cat.chapters.map((ch) => (
                    <button
                      key={ch.id}
                      onClick={() => setActiveChapterId(ch.id)}
                      className={`flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-xs transition-colors ${
                        activeChapterId === ch.id
                          ? 'bg-cyan-50 font-medium text-cyan-800 ring-1 ring-cyan-200'
                          : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                      }`}
                    >
                      <span className="text-sm">{ch.icon}</span>
                      <span className="truncate">{ch.title}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </nav>

        {/* Export footer */}
        <div className="border-t border-slate-100 px-4 py-3">
          <div className="relative">
            <button
              onClick={() => setShowExport(!showExport)}
              className="flex w-full items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-600 hover:bg-slate-50"
            >
              <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
              </svg>
              Export Guide
            </button>
            {showExport && (
              <div className="absolute bottom-full left-0 mb-1 w-full rounded-lg border border-slate-200 bg-white py-1 shadow-lg">
                <button
                  onClick={() => { downloadFile(guideToMarkdown(), 'rule42-billing-guide.md', 'text/markdown'); setShowExport(false); }}
                  className="flex w-full items-center gap-2 px-3 py-2 text-xs text-slate-700 hover:bg-slate-50"
                >
                  <span className="font-mono text-[10px] rounded bg-slate-100 px-1.5 py-0.5 text-slate-500">.md</span>
                  Markdown
                </button>
                <button
                  onClick={() => { downloadFile(guideToHtml(), 'rule42-billing-guide.html', 'text/html'); setShowExport(false); }}
                  className="flex w-full items-center gap-2 px-3 py-2 text-xs text-slate-700 hover:bg-slate-50"
                >
                  <span className="font-mono text-[10px] rounded bg-slate-100 px-1.5 py-0.5 text-slate-500">.html</span>
                  HTML (styled)
                </button>
                <button
                  onClick={() => { downloadFile(guideToJson(), 'rule42-billing-guide.json', 'application/json'); setShowExport(false); }}
                  className="flex w-full items-center gap-2 px-3 py-2 text-xs text-slate-700 hover:bg-slate-50"
                >
                  <span className="font-mono text-[10px] rounded bg-slate-100 px-1.5 py-0.5 text-slate-500">.json</span>
                  JSON (structured)
                </button>
              </div>
            )}
          </div>
        </div>
      </aside>

      {/* ─── Content Area ────────────────────────────────────────────── */}
      <main ref={contentRef} className="flex-1 overflow-y-auto bg-slate-50">
        {activeEntry ? (
          <div className="mx-auto max-w-3xl px-10 py-10">
            {/* Chapter header */}
            <div className="mb-8">
              <p className="text-xs font-bold uppercase tracking-wider text-slate-500">
                {activeEntry.cat.title}
              </p>
              <h2 className="mt-1.5 text-2xl font-bold text-slate-900">
                <span className="mr-2">{activeEntry.chapter.icon}</span>
                {activeEntry.chapter.title}
              </h2>
              <p className="mt-2.5 text-sm leading-relaxed text-slate-600">
                {activeEntry.chapter.description}
              </p>
            </div>

            {/* Sections */}
            <div className="space-y-8">
              {activeEntry.chapter.sections.map((section) => (
                <div
                  key={section.id}
                  id={section.id}
                  className="rounded-xl border border-slate-100 bg-white p-6 shadow-sm"
                >
                  <h3 className="text-base font-semibold text-slate-900">{section.title}</h3>
                  <div className="mt-3 text-sm leading-relaxed text-slate-600 whitespace-pre-line">
                    {section.content.split(/(\*\*[^*]+\*\*)/).map((part, i) => {
                      if (part.startsWith('**') && part.endsWith('**')) {
                        return <strong key={i} className="font-semibold text-slate-800">{part.slice(2, -2)}</strong>;
                      }
                      return <React.Fragment key={i}>{part}</React.Fragment>;
                    })}
                  </div>
                  {section.codeBlocks?.map((block) => {
                    const thisIdx = codeIdx++;
                    return (
                      <CodeBlock
                        key={thisIdx}
                        block={block}
                        globalCopyIdx={copiedIdx}
                        idx={thisIdx}
                        onCopy={handleCopy}
                      />
                    );
                  })}
                </div>
              ))}
            </div>

            {/* Chapter navigation */}
            <div className="mt-12 flex items-center justify-between border-t border-slate-100 pt-6">
              {(() => {
                const allChapters = GUIDE_CATEGORIES.flatMap(c => c.chapters);
                const currentIdx = allChapters.findIndex(c => c.id === activeChapterId);
                const prev = currentIdx > 0 ? allChapters[currentIdx - 1] : null;
                const next = currentIdx < allChapters.length - 1 ? allChapters[currentIdx + 1] : null;
                return (
                  <>
                    {prev ? (
                      <button
                        onClick={() => setActiveChapterId(prev.id)}
                        className="flex items-center gap-2 rounded-lg border border-slate-200 px-4 py-2.5 text-xs font-medium text-slate-600 hover:bg-slate-50"
                      >
                        <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
                        </svg>
                        <span>{prev.icon} {prev.title}</span>
                      </button>
                    ) : <div />}
                    {next ? (
                      <button
                        onClick={() => setActiveChapterId(next.id)}
                        className="flex items-center gap-2 rounded-lg border border-slate-200 px-4 py-2.5 text-xs font-medium text-slate-600 hover:bg-slate-50"
                      >
                        <span>{next.icon} {next.title}</span>
                        <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                        </svg>
                      </button>
                    ) : <div />}
                  </>
                );
              })()}
            </div>
          </div>
        ) : (
          <div className="flex h-full items-center justify-center">
            <p className="text-sm text-slate-400">Select a topic from the index.</p>
          </div>
        )}
      </main>
    </div>
    </AdminLayout>
  );
}
